import React from 'react';
import classnames from 'classnames';
import { Column } from 'react-table';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import route, { api } from '@configs/routes.json';

import { SampleDetail, SampleReport, IDownloadSampleReport } from '@models';

import { ReactComponent as ResultIcon } from '@assets/images/results_default.svg';

import { formatDateWithTimezone, DATE_OPTIONS, formatDateUTC } from '@utils';

import SampleTypeContent from '../../core/components/SampleTypeContent';

type OnClick = {
  onClick: () => void;
};

type IconTextOptions = OnClick & {
  iconClass: string;
  text: string;
};

const IconText = ({ iconClass, text, onClick }: IconTextOptions) => {
  const { t } = useTranslation();
  return (
    <div className='link' onClick={onClick}>
      <i className={classnames(iconClass, 'fcolor--primary-brand')}></i>{' '}
      {t(text)}
    </div>
  );
};

export const SelectAndDownload = ({ onClick }: OnClick) => (
  <IconText
    iconClass='fas fa-check-circle'
    text='sampleList_results_selectAndDownloadResults'
    onClick={onClick}
  ></IconText>
);

const renderResultsCell = (
  patientId: string,
  sampleReports: SampleReport[],
  t: TFunction,
  projectId: string
) => {
  if (sampleReports.length === 0) {
    return '';
  }

  sampleReports.sort((a, b) => +new Date(b.created) - +new Date(a.created));

  const activeReports = sampleReports.filter((r) => r.status === 'Active');
  const obsoletedReports = sampleReports
    .filter((r) => r.status === 'Obsolete')
    .map((r) => r.name);

  if (activeReports.length > 0) {
    const activeReport: IDownloadSampleReport = activeReports[0] as any;
    return (
      <div>
        <div className='sample-list-result active-result'>
          <ResultIcon className='poll-icon' title='result-icon' />
          <a
            href={`${api.samples.get
              .replace(':sampleId', activeReport.orderTestId)
              .replace(':type', activeReport.documentType)
              .replace(':name', activeReport.name)}?projectid=${projectId}`}
            download
          >
            {activeReport.name}
          </a>
        </div>
        {obsoletedReports.length > 0 ? (
          <div className='results-updated'>
            {t('results_cell_updatedFrom').replace(
              '{ids}',
              obsoletedReports.join(t('list_delimiter'))
            )}
          </div>
        ) : null}
      </div>
    );
  } else if (obsoletedReports.length > 0) {
    return (
      <div>
        <div className='results-obsoleted'>
          <span>
            {t('results_cell_obsolete').replace(
              '{ids}',
              obsoletedReports.join(t('list_delimiter'))
            )}
          </span>
        </div>
        <div className='sample-list-result'>
          <a
            target='_blank'
            rel='noopener noreferrer'
            href={`${route.app.results}?externalSubjectId=${encodeURIComponent(
              patientId
            )}`}
          >
            <span>{t('results_cell_findLatest')}</span>
            <i className='fas fa-chevron-right'></i>
          </a>
        </div>
      </div>
    );
  } else {
    return '';
  }
};

export const getColumns = (
  t: TFunction,
  timezone: string,
  projectId: string
): Column<SampleDetail>[] => {
  return [
    {
      accessor: 'sampleName',
      Header: t('orderDetail_sample_name').toString(),
      Cell: ({ value }) => <div className='bold'>{value}</div>,
      sortType: 'basic',
    },
    {
      accessor: 'patientId',
      Header: t('orderDetail_patient_id').toString(),
      Cell: ({ value }) => <div className='bold'>{value}</div>,
      sortType: 'basic',
    },
    {
      accessor: 'sampleId',
      Header: t('orderDetail_sample_id').toString(),
      sortDescFirst: true,
      sortType: 'basic',
    },
    {
      accessor: (originalRow) =>
        originalRow.collectionDate
          ? new Date(originalRow.collectionDate)
          : new Date(0),
      id: 'collectionDate',
      Header: t('orderDetail_collection_date').toString(),
      Cell: ({ value }: { value: Date }) =>
        value.getTime() !== 0 ? formatDateUTC(value, DATE_OPTIONS) : '',
      sortType: 'datetime',
      sortDescFirst: true,
    },
    {
      accessor: 'testType',
      Header: t('orderDetail_test_type').toString(),
      Cell: ({ row, row: { values } }) => {
        return (
          <SampleTypeContent
            sampleType={values.testType}
            warning={{
              status: row.original.status,
              message: t('orderDetail_test_waiting'),
            }}
          />
        );
      },
      sortType: ({ values: rowA }, { values: rowB }) => {
        const sampleStringA = `${rowA.testType ?? ' '}${rowA.status}`;
        const sampleStringB = `${rowB.testType ?? ' '}${rowB.status}`;
        return sampleStringA.localeCompare(sampleStringB);
      },
    },

    {
      accessor: 'qcStatus',
      Header: t('orderDetail_qc_status').toString(),
      Cell: ({ value }): JSX.Element => {
        const obj: {
          text: string;
          textClass: string[];
          img: undefined | string;
          imgClass: string;
        } = {
          text: '',
          textClass: ['text-uppercase'],
          img: undefined,
          imgClass: '',
        };

        switch (value.toUpperCase()) {
          case 'PASSED':
            obj.text = t('orderDetail_qc_status_pass');
            obj.img = 'fa-check-circle';
            obj.imgClass = 'pass';
            break;
          case 'FAILED':
            obj.text = t('orderDetail_qc_status_fail');
            obj.img = 'fa-times-circle';
            obj.imgClass = 'fail';
            break;
          case 'CANCELLED':
            obj.text = t('orderDetail_qc_status_cancelled');
            break;
          case 'PENDING':
            obj.text = t('orderDetail_qc_status_pending');
            obj.textClass.push('font-italic');
            break;
        }
        return (
          <>
            {obj.img && (
              <span className={`order-detail-qc-icon mr-1 ${obj.imgClass}`}>
                <i className={`fas ${obj.img}`}></i>
              </span>
            )}
            <span className={obj.textClass.join(' ')}>{obj.text}</span>
          </>
        );
      },
      sortType: 'basic',
    },

    {
      accessor: (originalRow) =>
        originalRow.resultDate ? new Date(originalRow.resultDate) : new Date(0),
      id: 'resultDate',
      Header: t('orderDetail_result_date').toString(),
      Cell: ({ value }: { value: Date }) =>
        value.getTime() !== 0 ? formatDateWithTimezone(value, timezone) : '',
      sortType: 'datetime',
      sortDescFirst: true,
    },
    {
      accessor: (row) => ({
        patientId: row.patientId,
        reports: row.reports,
      }),
      id: 'reports',
      Header: t('orderDetail_results').toString(),
      Cell: ({
        value: { patientId, reports },
      }: {
        value: { patientId: string; reports: SampleReport[] };
      }) => renderResultsCell(patientId, reports, t, projectId),
      disableSortBy: true,
    },
  ];
};
